import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import {
  Box,
  Button,
  MenuItem,
  Menu,
  IconButton,
} from '@mui/material'
import { DateInput, TextInput, useListContext, useTranslate } from 'react-admin'
import ContentFilter from '@mui/icons-material/FilterList'
import HighlightOff from '@mui/icons-material/HighlightOff'
import isEqual from 'lodash/isEqual'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { supplierServices } from 'src/services/reservation/supplier/supplier.service'

export const SupplierConciliationFilters = ({ listFilters }) => {
  const { displayedFilters = {}, setFilters, showFilter } = useListContext()
  const translate = useTranslate()
  const hasDisplayedFilters = !isEqual(displayedFilters, {})
  const [open, setOpen] = useState(false)
  const anchorEl = useRef()

  const handleClickButton = useCallback(
    (event) => {
      event.preventDefault()
      setOpen(true)
      anchorEl.current = event.currentTarget
    },
    [anchorEl, setOpen]
  )

  const handleRequestClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleShow = useCallback(
    ({ source, defaultValue }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue)
      setOpen(false)
    },
    [showFilter, setOpen]
  )

  return (
    <Box>
      <Button
        className='add-filter'
        label='ra.action.add_filter'
        aria-haspopup='true'
        onClick={handleClickButton}
        size='small'
      >
        <ContentFilter /> {translate('ra.action.add_filter')}
      </Button>
      <Menu open={open} anchorEl={anchorEl.current} onClose={handleRequestClose}>
        {Array.isArray(listFilters) &&
          listFilters.map(
            (item, index) =>
              !displayedFilters?.[item.source] && (
                <MenuItem
                  onClick={() => {
                    setOpen(false)
                    handleShow({ source: item.source })
                  }}
                  key={index}
                >
                  {translate(`${item.name}`)}
                </MenuItem>
              )
          )}

        {hasDisplayedFilters && (
          <MenuItem onClick={() => setFilters({}, {}, false)}>
            {translate('ra.action.remove_all_filters')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

const RemoveFilterField = ({ children, prop, fields, width = null, maxWidth = null, minWidth = null, field }) => {
  const { displayedFilters = {}, setFilters } = useListContext()
  const { setValue, getValues } = useFormContext()
  const translate = useTranslate()

  const onClickButton = () => {
    const copy = { ...displayedFilters }
    delete copy[prop]

    if (Array.isArray(fields) && fields.length > 0) {
      fields.forEach((f) => setValue(f, undefined))
      let values = getValues()
      setFilters(values, copy, false)
    }
  }

  return (
    <div>
      <label style={{ marginLeft: '10px', fontSize: '12px' }}>{translate(`ra.field.${field}`)}</label>
      <Box
        display={'flex'}
        alignItems={'flex-start'}
        marginRight={5}
        width={width}
        marginTop={'10px'}
        maxWidth={maxWidth}
        minWidth={minWidth}
      >
        <Box width={'34px'} paddingTop={1}>
          <IconButton size='small' onClick={onClickButton}>
            <HighlightOff />
          </IconButton>
        </Box>
        <Box width={'calc(100% - 34px)'} display={'flex'} minHeight={'60px'} height={'auto'}>
          {children}
        </Box>
      </Box>
    </div>
  )
}

const SupplierInput = ({ label, source }) => {
  const [loadingSupplierList, setLoadingSupplierList] = useState(false)
  const [supplierList, setSupplierList] = useState([])

  const getSupplier = async (name) => {
    try {
      setLoadingSupplierList(true)
      const list = await supplierServices.getByParam({ name })
      setSupplierList(list || [])
      setLoadingSupplierList(false)
    } catch (error) {
      setSupplierList([])
      setLoadingSupplierList(false)
    }
  }

  return (
    <AutocompleteInput
      label={label}
      useOptionValue={true}
      source={source}
      options={supplierList}
      loading={loadingSupplierList}
      optionsText={['name']}
      onSearch={getSupplier}
      resetOnLoad={false}
      style={{ width: '100%' }}
    />
  )
}

export const PostFilterFormSupplierConciliation = () => {
  const { displayedFilters = {}, filterValues, setFilters } = useListContext()
  const translate = useTranslate()
  const form = useForm()

  useEffect(() => {
    Object.entries(filterValues).forEach(([key, value]) => {
      form.setValue(key, value)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (displayedFilters && Object.keys(displayedFilters).length === 0) {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFilters])

  const onSubmit = (values) => {
    if (Object.keys(values).length > 0) {
      setFilters(values, displayedFilters, false)
    }
  }

  const resetFilter = () => {
    setFilters({}, {}, false)
  }

  const areFilterActivate = (displayedFilters) => {
    if (displayedFilters && Object.keys(displayedFilters).length > 0) {
      const props = Object.values(displayedFilters)
      return props.some((prop) => prop === true)
    }
    return false
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' mb={1} height={'auto'}>
          <Box width={'calc(100% - 156px)'} display={'flex'} flexWrap={'wrap'}>
            {displayedFilters?.name && (
              <RemoveFilterField
                field={'name'}
                prop={'name'}
                fields={['name']}
                width={300}
              >
                <TextInput
                  label={translate('ra.field.name')}
                  fullWidth
                  source='name'
                />
              </RemoveFilterField>
            )}

            {displayedFilters?.created_at && (
              <RemoveFilterField
                prop={'created_at'}
                fields={['created_at__gte', 'created_at__lte']}
                width={500}
                field={'created_at'}
              >
                <DateInput
                  label={translate('ra.field.from')}
                  fullWidth
                  source='created_at__gte'
                />
                <DateInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.to')}
                  fullWidth
                  source='created_at__lte'
                />
              </RemoveFilterField>
            )}

            {displayedFilters?.supplier_id && (
              <RemoveFilterField
                field={'supplier'}
                prop={'supplier_id'}
                fields={['supplier_id']}
                width={300}
              >
                <SupplierInput label={translate('ra.field.supplier')} source={'supplier_id'} />
              </RemoveFilterField>
            )}

            {displayedFilters?.general_status && (
              <RemoveFilterField
                field={'general_status'}
                prop={'general_status'}
                fields={['general_status']}
                width={250}
              >
                <SelectInput
                  label={translate('ra.field.general_status') || 'Estado'}
                  source='general_status'
                  options={[
                    { id: 'OPEN', name: translate('ra.status.open_fem') || translate('ra.status.open') },
                    { id: 'CLOSED', name: translate('ra.status.closed_fem') || translate('ra.status.closed') || 'Cerrada' },
                    { id: 'PROCESSING', name: translate('ra.status.processing') },
                    { id: 'DELETED', name: translate('ra.status.deleted_fem') || translate('ra.status.deleted') },
                    { id: 'COMPLETED', name: translate('ra.status.completed_fem') || translate('ra.status.completed') },
                  ]}
                  optionsText={['name']}
                  optionValue='id'
                  style={{ width: '100%' }}
                />
              </RemoveFilterField>
            )}
          </Box>
          <Box
            width={'156px'}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
          >
            {areFilterActivate(displayedFilters) && (
              <>
                <Box mr={2} paddingY={'10px'}>
                  <Button variant='outlined' color='primary' type='submit'>
                    {translate('ra.action.filter')}
                  </Button>
                </Box>
                <Box paddingY={'10px'}>
                  <Button variant='outlined' onClick={resetFilter}>
                    {translate('ra.action.close')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </form>
    </FormProvider>
  )
}
